// Dots (a.k.a. Junk / Trash / Garbage) — the net-new side game. The group picks
// WHICH dots count on the setup checklist. Birdie/eagle auto-count from scores
// (eagle-or-better = 2 dots, birdie = 1); greenie/sandie/barkie/chippy/polly are
// tapped per hole per player on the scorecard. Settlement: total dots per player
// → zero-sum pairwise × dotValue (same pairwise the other web games use).
//
// Contract (WOLF_SYNC_CONTRACT.md):
//   meta.addons.dots = { enabled, dotValue, dotsInPlay:[dotKey] }
//   play node        = dots/{hole}/{playerId} = [dotKey]   (tapped junk dots)
import { pairwise } from './ggPlayMoney.js'

export const DOT_CATALOG = [
  { key: 'birdie',  label: 'Birdie',  emoji: '🐦', auto: true,  default: true,  desc: '1-under par — auto from score (1 dot)' },
  { key: 'eagle',   label: 'Eagle',   emoji: '🦅', auto: true,  default: true,  desc: '2-under or better — auto from score (2 dots)' },
  { key: 'greenie', label: 'Greenie', emoji: '🟢', auto: false, default: true,  desc: 'closest to the pin on a par 3 in regulation' },
  { key: 'sandie',  label: 'Sandie',  emoji: '🏖️', auto: false, default: true,  desc: 'par or better out of a bunker' },
  { key: 'barkie',  label: 'Barkie',  emoji: '🌳', auto: false, default: false, desc: 'par after hitting a tree' },
  { key: 'chippy',  label: 'Chippy',  emoji: '⛳', auto: false, default: false, desc: 'chip-in from off the green' },
  { key: 'polly',   label: 'Polly',   emoji: '🎯', auto: false, default: false, desc: 'par after going out of bounds' },
]
export const TAP_DOTS = DOT_CATALOG.filter((c) => !c.auto).map((c) => c.key)
export const AUTO_DOTS = DOT_CATALOG.filter((c) => c.auto).map((c) => c.key)
const BY_KEY = Object.fromEntries(DOT_CATALOG.map((c) => [c.key, c]))
export const dotLabel = (k) => BY_KEY[k] || { key: k, label: k, emoji: '•', auto: false }

// Per-player dot totals + zero-sum settlement from a game object, or null when
// Dots is off / not viable. Combines auto birdie/eagle (from entered scores,
// only for selected auto dots) with tapped dots (only tap-types still in play).
export function dotsState(game) {
  const d = game?.meta?.addons?.dots
  if (!d?.enabled) return null
  const dotsInPlay = d.dotsInPlay || []
  const dotValue = Number(d.dotValue) || 0
  const players = game.players || []
  if (players.length < 2) return null
  const pars = game.meta.course?.pars || []
  const scores = game.scores || {}
  const dotsNode = game.dots || {}
  const autoBirdie = dotsInPlay.includes('birdie')
  const autoEagle = dotsInPlay.includes('eagle')

  const total = {}
  const breakdown = {}
  players.forEach((p) => { total[p.id] = 0; breakdown[p.id] = {} })
  const add = (pid, key, n) => { total[pid] += n; breakdown[pid][key] = (breakdown[pid][key] || 0) + n }

  // Auto birdie / eagle from entered gross scores (sparse — unscored = skipped).
  for (let h = 1; h <= 18; h++) {
    const par = pars[h - 1] ?? 4
    players.forEach((p) => {
      const g = Number(scores[h]?.[p.id])
      if (!Number.isFinite(g) || g <= 0) return
      const diff = par - g
      if (diff >= 2 && autoEagle) add(p.id, 'eagle', 2)
      else if (diff === 1 && autoBirdie) add(p.id, 'birdie', 1)
    })
  }
  // Tapped junk dots — count only tap-types that are still in play.
  for (const h of Object.keys(dotsNode)) {
    const rec = dotsNode[h] || {}
    for (const pid of Object.keys(rec)) {
      if (total[pid] == null) continue
      const arr = Array.isArray(rec[pid]) ? rec[pid] : []
      for (const key of arr) if (TAP_DOTS.includes(key) && dotsInPlay.includes(key)) add(pid, key, 1)
    }
  }

  const dotsArr = players.map((p) => total[p.id] || 0)
  const nets = pairwise(dotsArr, dotValue)
  const rows = players.map((p, i) => ({ id: p.id, name: p.name, dots: dotsArr[i], net: nets[i], breakdown: breakdown[p.id] }))
  return { rows, dotValue, dotsInPlay, total: dotsArr.reduce((a, b) => a + b, 0) }
}
